import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import { Button } from "../../../../components/ui/button";
import { Link } from "react-router-dom";
import {
  getTemplateTypeLabel,
  TemplateType,
  WorkPaperTemplate,
} from "../../../../models/workPaperTemplate";
import { LoadingSpinner } from "../../../../components/ui/loadingSpinner/loadingSpinner";

interface TemplateSelectorProps {
  templates: WorkPaperTemplate[];
  // The kind `templates` was filtered to, named when there are none.
  templateType: TemplateType;
  selectedTemplateId: string | null;
  onTemplateSelect: (templateId: string | null) => void;
  loading: boolean;
  defaultTemplateId?: string | null;
  onSetAsDefault?: () => void;
  settingDefault?: boolean;
}

export const TemplateSelector = ({
  templates,
  templateType,
  selectedTemplateId,
  onTemplateSelect,
  loading,
  defaultTemplateId,
  onSetAsDefault,
  settingDefault = false,
}: TemplateSelectorProps) => {
  if (loading) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">Select Template:</span>
        <LoadingSpinner />
      </div>
    );
  }

  if (templates.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">
        No {getTemplateTypeLabel(templateType)} templates yet.{" "}
        <Link
          to={`/audit/work-paper-templates/new?type=${templateType}`}
          className="underline"
          data-testid="create-template-link"
        >
          Create one
        </Link>{" "}
        to get started.
      </div>
    );
  }

  const showSetDefaultButton =
    selectedTemplateId &&
    selectedTemplateId !== defaultTemplateId &&
    onSetAsDefault;

  return (
    <div className="flex items-center gap-4">
      <span className="text-sm font-medium">Select Template:</span>
      <Select
        value={selectedTemplateId || undefined}
        onValueChange={(value) => onTemplateSelect(value)}
      >
        <SelectTrigger className="w-[400px]">
          <SelectValue placeholder="Choose a workpaper template" />
        </SelectTrigger>
        <SelectContent>
          {templates.map((template) => (
            <SelectItem key={template.id} value={template.id}>
              {template.name}
              {template.isBaseTemplate && (
                <span className="ml-2 text-xs text-muted-foreground">
                  (Base)
                </span>
              )}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {showSetDefaultButton && (
        <Button
          variant="outline"
          size="sm"
          onClick={onSetAsDefault}
          loading={settingDefault}
        >
          Set as default
        </Button>
      )}
    </div>
  );
};
